import { Injectable, signal } from '@angular/core';
import { ProjectsService, SqlService } from '@services';
import { Project, Phase, Task, ProjectDomain, PhasesDomain, TasksDomain } from '@domain';
import { catchError, finalize, forkJoin, of, tap } from 'rxjs';
import { FormControl } from '@angular/forms';
import { AuthenticationFeatureService } from '../authentication-feature/authentication-feature.service';

export interface TaskNode extends Task {
  name: string;
  children?: TaskNode[];
}

export interface PhaseNode extends Phase {
  name: string;
  children?: TaskNode[];
}

@Injectable({
  providedIn: 'root',
})
export class ProjectsFeatureService {
  projects = signal<Project[]>([]);
  isLoadingProjects = signal<boolean>(false);

  phases = signal<Phase[]>([]);
  isLoadingPhases = signal<boolean>(false);

  tasks = signal<Task[]>([]);
  isLoadingTasks = signal<boolean>(false);

  filterProjects = signal<{ projectNo: string; phaseNo: string; taskNo: string }>({
    projectNo: '',
    phaseNo: '',
    taskNo: '',
  });

  searchText = signal<string>('');

  constructor(
    private _projectsService: ProjectsService,
    private _sqlService: SqlService,
    private _authFeature: AuthenticationFeatureService,
  ) {}

  /**
   * @description fetch projects list from api service, filtered to only projects the logged-in user is a member of
   * */
  fetchProjects() {
    const email = this._authFeature.getInstance().getActiveAccount()?.username ?? '';
    this.isLoadingProjects.update(() => true);
    return forkJoin({
      projectRoot: this._projectsService.fetchProjects(),
      teamMembers: this._projectsService.fetchProjectTeamMembers(email),
    }).pipe(
      tap(({ projectRoot, teamMembers }) => {
        const memberProjectNos = new Set(teamMembers.value.map((m) => m.projectNo));
        const allProjects = new ProjectDomain(projectRoot.value).newProjects;
        this.projects.update(() => allProjects.filter((p) => memberProjectNos.has(p.projectNo)));
      }),
      finalize(() => this.isLoadingProjects.update(() => false)),
    );
  }

  /**
   * @description fetch phases list from api service, enriched with WorkItemId from SQL
   * */
  fetchPhases() {
    this.isLoadingPhases.update(() => true);
    return forkJoin({
      phaseRoot: this._projectsService.fetchPhase(),
      features: this._sqlService.fetchFeatures().pipe(catchError(() => of([]))),
    }).pipe(
      tap(({ phaseRoot, features }) => {
        const workItemMap = new Map(features.map((f) => [f.PhaseNo, f.WorkItemId]));
        const phases = new PhasesDomain(phaseRoot.value).newPhases.map((phase) => ({
          ...phase,
          workItemId: workItemMap.get(phase.phaseNo) ?? null,
        }));
        this.phases.set(phases);
      }),
      finalize(() => this.isLoadingPhases.update(() => false)),
    );
  }

  /**
   * @description fetch tasks list from api service, enriched with WorkItemId from SQL
   * */
  fetchTasks() {
    this.isLoadingTasks.update(() => true);
    return forkJoin({
      taskRoot: this._projectsService.fetchTask(),
      pbis: this._sqlService.fetchPbis().pipe(catchError(() => of([]))),
    }).pipe(
      tap(({ taskRoot, pbis }) => {
        const workItemMap = new Map(pbis.map((p) => [p.TaskNo, p.WorkItemId]));
        const tasks = new TasksDomain(taskRoot.value).newTasks.map((task) => ({
          ...task,
          workItemId: workItemMap.get(task.taskNo) ?? null,
        }));
        this.tasks.set(tasks);
      }),
      finalize(() => this.isLoadingTasks.update(() => false)),
    );
  }

  /**
   * @description filter phase list by project no
   * */
  filterPhasesByProjectNo(phases: Phase[], projectNo?: string | null): Phase[] {
    return projectNo ? phases.filter((phase) => phase.projectNo === projectNo) : [];
  }

  /**
   * @description filter task list by phase no
   * */
  filterTaskByPhaseNoAndProjectNo(tasks: Task[], phaseNo?: string | null, projectNo?: string | null): Task[] {
    return phaseNo && projectNo ? tasks.filter((task) => task.phaseNo === phaseNo && task.projectNo === projectNo) : [];
  }

  /**
   * @description generate data for a tree list in a phase component
   * */
  generateTreeDataSourceForPhases(projectNo: string): PhaseNode[] {
    const { taskNo, phaseNo } = this.filterProjects();
    const search = this.searchText().toLowerCase();

    let phasesFilteredList = this.filterPhasesByProjectNo(this.phases(), projectNo);

    if (phaseNo && phaseNo.length > 0) {
      phasesFilteredList = phasesFilteredList.filter((phase) => phaseNo.includes(phase.phaseNo));
    }

    return phasesFilteredList
      .map((phase) => {
        let tasksFilteredList = this.filterTaskByPhaseNoAndProjectNo(this.tasks(), phase.phaseNo, projectNo);

        if (taskNo && taskNo.length > 0) {
          tasksFilteredList = tasksFilteredList.filter((task) => taskNo.includes(task.taskNo));
        }

        if (search) {
          tasksFilteredList = tasksFilteredList.filter(
            (task) =>
              task.taskNo.toLowerCase().includes(search) ||
              task.taskName.toLowerCase().includes(search) ||
              (task.workItemId != null && task.workItemId.toString().includes(search)),
          );
        }

        return {
          ...phase,
          name: phase.phaseName,
          children: tasksFilteredList.map((task) => ({ ...task, name: task.taskName })),
        };
      })
      .filter((phase) => {
        if (!search) return true;
        const phaseMatches =
          phase.phaseNo.toLowerCase().includes(search) ||
          phase.phaseName.toLowerCase().includes(search) ||
          (phase.workItemId != null && phase.workItemId.toString().includes(search));
        return phaseMatches || phase.children!.length > 0;
      });
  }

  displayProjectInputValue(selectedProject: Project): string {
    return selectedProject ? `${selectedProject.projectNo} - ${selectedProject.projectName}` : '';
  }

  displayPhaseInputValue(selectedPhase: Phase): string {
    return selectedPhase ? `${selectedPhase.phaseNo} - ${selectedPhase.phaseName}` : '';
  }

  displayTaskInputValue(selectedTask: Task): string {
    return selectedTask ? `${selectedTask.taskNo} - ${selectedTask.taskName}` : '';
  }

  resetProjectsFilter() {
    this.filterProjects.set({ projectNo: '', phaseNo: '', taskNo: '' });
    this.searchText.set('');
  }
}
